import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  message,
  Card,
  Tag,
  Tooltip,
  Typography,
  Row,
  Col,
  InputNumber,
  DatePicker,
  Alert,
  App,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import {
  Plus,
  RefreshCw,
  Search,
  ArrowDownCircle,
  ArrowUpCircle,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Edit,
  Trash2,
} from "lucide-react";
import dayjs from "dayjs";
import ErrorBoundary from "../common/ErrorBoundary";
import apiService from "../../services/api";
import { formatDateTime } from "../../utils/formatters";
import { useAuth } from "../../hooks/useAuth";
import "./Transaction.css";

const { Option } = Select;
const { TextArea } = Input;
const { Text, Title } = Typography;

// 交易类型配置
const transactionTypes = [
  { value: "deposit", label: "充值", icon: ArrowDownCircle, color: "green", isPositive: true },
  { value: "withdraw", label: "提现", icon: ArrowUpCircle, color: "red", isPositive: false },
  { value: "borrow", label: "借入", icon: TrendingUp, color: "blue", isPositive: true },
  { value: "repay", label: "归还", icon: TrendingDown, color: "purple", isPositive: false },
  { value: "transfer_in", label: "转入", icon: ArrowDownCircle, color: "cyan", isPositive: true },
  { value: "transfer_out", label: "转出", icon: ArrowUpCircle, color: "orange", isPositive: false },
  { value: "internal_transfer", label: "内部划转", icon: ArrowDownCircle, color: "default", isPositive: null },
  { value: "trade_profit", label: "交易盈利", icon: TrendingUp, color: "green", isPositive: true },
  { value: "trade_loss", label: "交易亏损", icon: TrendingDown, color: "red", isPositive: false },
  { value: "fee", label: "手续费", icon: DollarSign, color: "volcano", isPositive: false },
  { value: "interest", label: "利息", icon: DollarSign, color: "gold", isPositive: false },
  { value: "adjustment", label: "调整", icon: TrendingUp, color: "geekblue", isPositive: null },
  { value: "other", label: "其他", icon: DollarSign, color: "default", isPositive: null },
];

const TransactionList = () => {
  const [transactions, setTransactions] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  const [accountConfigs, setAccountConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [balanceFetching, setBalanceFetching] = useState(false);
  const [stats, setStats] = useState({
    total_records: 0,
    total_inflow: 0,
    total_outflow: 0,
    net_flow: 0,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [searchParams, setSearchParams] = useState({
    account: "",
    exchange: "",
    trade_type: "",
    asset: "",
    type: "",
    date_from: "",
    date_to: "",
  });

  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [form] = Form.useForm();
  const formAccount = Form.useWatch("account", form);
  const formExchange = Form.useWatch("exchange", form);
  const formTradeType = Form.useWatch("trade_type", form);
  const formType = Form.useWatch("type", form);

  // 内部划转模式：现货↔合约，转出方资金减少、转入方资金增加（仅新增时启用）
  const isInternalTransferMode = !editingId && formType === "internal_transfer";
  const transferToTradeType = formTradeType === "spot" ? "swap" : "spot";
  const transferTargetMissing =
    isInternalTransferMode &&
    !!formAccount &&
    !!formExchange &&
    !!formTradeType &&
    !accountConfigs.some(
      (c) =>
        c.account === formAccount &&
        c.exchange === formExchange &&
        c.trade_type === transferToTradeType,
    );

  const loadTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        page_size: pagination.pageSize,
        ...searchParams,
      };
      // 过滤空值
      Object.keys(params).forEach((key) => {
        if (params[key] === "" || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await apiService.transactions.getTransactions(params);
      if (response.data?.data) {
        setTransactions(response.data.data.list || []);
        setPagination((prev) => ({
          ...prev,
          total: response.data.data.total || 0,
        }));
      }
    } catch (err) {
      message.error(
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message ||
        "加载流水失败",
      );
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, searchParams]);

  const loadStats = async () => {
    try {
      const params = { ...searchParams };
      Object.keys(params).forEach((key) => {
        if (params[key] === "" || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await apiService.transactions.getStats(params);
      if (response.data?.data) {
        setStats(response.data.data);
      }
    } catch (err) {
      console.error("加载统计失败:", err);
    }
  };

  const loadExchanges = async () => {
    try {
      const response = await apiService.exchanges.getActiveExchanges();
      if (response.data?.data) {
        setExchanges(response.data.data || []);
      }
    } catch (err) {
      console.error("加载交易所失败:", err);
    }
  };

  const loadAccountConfigs = async () => {
    try {
      const response = await apiService.accountConfigs.getConfigs({ page_size: 200, is_active: true });
      if (response.data?.data) {
        setAccountConfigs(response.data.data.list || []);
      }
    } catch (err) {
      console.error("加载账号配置失败:", err);
    }
  };

  useEffect(() => {
    loadTransactions();
    loadStats();
    loadExchanges();
    loadAccountConfigs();
  }, [loadTransactions]);

  const handleSubmit = async (values) => {
    try {
      const transactedAt =
        values.transacted_at?.format("YYYY-MM-DD HH:mm:ss") ||
        dayjs().format("YYYY-MM-DD HH:mm:ss");

      if (editingId) {
        await apiService.transactions.updateTransaction(editingId, {
          ...values,
          transacted_at: transactedAt,
        });
        message.success("流水记录更新成功");
      } else if (values.type === "internal_transfer") {
        // 内部划转：后端在同一事务内生成转出（-N）与转入（+N）两条流水
        await apiService.transactions.createInternalTransfer({
          account: values.account,
          exchange: values.exchange,
          asset: values.asset,
          amount: values.amount,
          from_trade_type: values.trade_type,
          to_trade_type: values.trade_type === "spot" ? "swap" : "spot",
          remark: values.remark,
          transacted_at: transactedAt,
        });
        message.success("内部划转成功，已生成转出与转入两条流水");
      } else {
        await apiService.transactions.createTransaction({
          ...values,
          transacted_at: transactedAt,
        });
        message.success("流水记录创建成功");
      }

      setShowModal(false);
      setEditingId(null);
      form.resetFields();
      loadTransactions();
      loadStats();
    } catch (err) {
      message.error(
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message,
      );
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "确认删除",
      content: "确定要删除这条资金流水记录吗？此操作不可逆。",
      okText: "确认",
      cancelText: "取消",
      okType: "danger",
      onOk: async () => {
        try {
          await apiService.transactions.deleteTransaction(id);
          message.success("删除成功");
          loadTransactions();
          loadStats();
        } catch (err) {
          message.error(
            err.response?.data?.error?.message ||
            err.response?.data?.message ||
            "删除失败"
          );
        }
      },
    });
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    form.resetFields();
    form.setFieldsValue({
      account: record.account,
      exchange: record.exchange,
      trade_type: record.trade_type,
      asset: record.asset,
      type: record.type,
      amount: record.amount,
      balance_before: record.balance_before,
      balance_after: record.balance_after,
      transacted_at: record.transacted_at ? dayjs(record.transacted_at) : dayjs(),
      remark: record.remark,
    });
    setShowModal(true);
  };

  const openModal = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({
      trade_type: "spot",
      type: "deposit",
      transacted_at: dayjs(),
    });
    setShowModal(true);
  };

  // 表单余额变动量：内部划转时表单展示转出方余额，金额按减少计
  const resolveBalanceDelta = (amount) => {
    const current = amount ?? 0;
    const internalTransfer =
      !editingId && form.getFieldValue("type") === "internal_transfer";
    return internalTransfer ? -current : current;
  };

  const fetchAndApplyBalance = async (account, exchange, tradeType, asset, amount, date) => {
    if (!account || !exchange || !tradeType || !asset) return;
    setBalanceFetching(true);
    try {
      const response = await apiService.transactions.getBalanceSummary({
        account,
        exchange,
        trade_type: tradeType,
        asset,
        date: date ? date.format("YYYY-MM-DD") : undefined,
      });
      const list = response.data?.data || [];
      const balanceBefore = list.length > 0 ? (list[0].balance ?? 0) : 0;
      const currentAmount = amount ?? form.getFieldValue("amount") ?? 0;
      form.setFieldsValue({
        balance_before: balanceBefore,
        balance_after: balanceBefore + resolveBalanceDelta(currentAmount),
      });
    } catch {
      // 查询失败不影响主流程
    } finally {
      setBalanceFetching(false);
    }
  };

  const handleFormValuesChange = (changedValues, allValues) => {
    if (editingId) return;

    const { account, exchange, trade_type, asset, amount, transacted_at } = allValues;

    if ("account" in changedValues) {
      const matched = accountConfigs.filter((c) => c.account === account);
      // 交易所/交易类型唯一时自动选上；账号同时有现货+合约配置时交易所仍可自动带出
      const uniqueExchanges = [...new Set(matched.map((c) => c.exchange))];
      const uniqueTradeTypes = [...new Set(matched.map((c) => c.trade_type))];
      const nextExchange = uniqueExchanges.length === 1 ? uniqueExchanges[0] : undefined;
      const nextTradeType = uniqueTradeTypes.length === 1 ? uniqueTradeTypes[0] : undefined;
      form.setFieldsValue({ exchange: nextExchange, trade_type: nextTradeType });
      if (nextExchange && nextTradeType) {
        fetchAndApplyBalance(account, nextExchange, nextTradeType, asset, amount, transacted_at);
      } else {
        form.setFieldsValue({ balance_before: undefined, balance_after: undefined });
      }
      return;
    }

    if ("exchange" in changedValues) {
      const matched = accountConfigs.filter(
        (c) => c.account === account && c.exchange === exchange,
      );
      if (matched.length === 1) {
        form.setFieldsValue({ trade_type: matched[0].trade_type });
        fetchAndApplyBalance(account, exchange, matched[0].trade_type, asset, amount, transacted_at);
      } else {
        fetchAndApplyBalance(account, exchange, trade_type, asset, amount, transacted_at);
      }
      return;
    }

    if ("trade_type" in changedValues || "asset" in changedValues || "transacted_at" in changedValues) {
      fetchAndApplyBalance(account, exchange, trade_type, asset, amount, transacted_at);
      return;
    }

    if ("amount" in changedValues || "type" in changedValues) {
      const balanceBefore = form.getFieldValue("balance_before") ?? 0;
      form.setFieldsValue({ balance_after: balanceBefore + resolveBalanceDelta(amount) });
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination({
      ...pagination,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    loadTransactions();
    loadStats();
  };

  const handleReset = () => {
    setSearchParams({
      account: "",
      exchange: "",
      trade_type: "",
      asset: "",
      type: "",
      date_from: "",
      date_to: "",
    });
    setPagination({ ...pagination, current: 1 });
  };

  const getTypeConfig = (type) => {
    return transactionTypes.find((t) => t.value === type) || transactionTypes[transactionTypes.length - 1];
  };

  const formatAmount = (amount, type) => {
    const config = getTypeConfig(type);
    const isPositive = config.isPositive === null ? amount >= 0 : config.isPositive;
    const color = isPositive ? "#52c41a" : "#ff4d4f";
    const prefix = isPositive ? "+" : "";

    return (
      <span style={{ color, fontWeight: 600 }}>
        {prefix}
        {amount?.toFixed(8) || "0"}
      </span>
    );
  };

  const columns = [
    {
      title: "时间",
      dataIndex: "transacted_at",
      key: "transacted_at",
      width: 160,
      render: (date) => (
        <Text className="trans-time">
          {formatDateTime(date)}
        </Text>
      ),
    },
    {
      title: "账号信息",
      key: "account_info",
      width: 180,
      render: (_, record) => (
        <div className="account-info-cell">
          <div className="account-name">{record.account}</div>
          <Text type="secondary" className="exchange-text">
            {record.exchange} · {record.trade_type === "spot" ? "现货" : "合约"}
          </Text>
        </div>
      ),
    },
    {
      title: "资产",
      dataIndex: "asset",
      key: "asset",
      width: 80,
      render: (asset) => <Tag className="asset-tag">{asset}</Tag>,
    },
    {
      title: "类型",
      dataIndex: "type",
      key: "type",
      width: 100,
      render: (type) => {
        const config = getTypeConfig(type);
        const Icon = config.icon;
        return (
          <Tag className={`type-tag ${config.color}`}>
            <Icon size={12} />
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: "变动金额",
      dataIndex: "amount",
      key: "amount",
      width: 150,
      align: "right",
      render: (amount, record) => formatAmount(amount, record.type),
    },
    {
      title: "变动前",
      dataIndex: "balance_before",
      key: "balance_before",
      width: 120,
      align: "right",
      render: (val) => val?.toFixed(8) || "-",
    },
    {
      title: "变动后",
      dataIndex: "balance_after",
      key: "balance_after",
      width: 120,
      align: "right",
      render: (val) => val?.toFixed(8) || "-",
    },
    {
      title: "备注",
      dataIndex: "remark",
      key: "remark",
      ellipsis: true,
      render: (text) => text || "-",
    },
    {
      title: "操作人",
      dataIndex: "operator",
      key: "operator",
      width: 100,
      render: (text) => text || "-",
    },
    {
      title: "操作",
      key: "actions",
      width: 120,
      fixed: "right",
      render: (_, record) => (
        isAdmin ? (
          <Space size="middle">
            <Tooltip title="编辑">
              <Button
                type="text"
                icon={<Edit size={16} className="text-blue-500" />}
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
            <Tooltip title="删除">
              <Button
                type="text"
                danger
                icon={<Trash2 size={16} />}
                onClick={() => handleDelete(record.id)}
              />
            </Tooltip>
          </Space>
        ) : null
      ),
    },
  ];

  return (
    <ErrorBoundary>
      <div className="transaction-page">
        {/* 页面头部 */}
        <div className="page-header">
          <div className="header-content">
            <Title level={2} className="page-title">
              <DollarSign className="title-icon" size={32} />
              资金流水
            </Title>
            <Text className="page-subtitle">
              查看所有资金变动记录
            </Text>
          </div>
          <Button
            type="primary"
            size="large"
            icon={<Plus size={18} />}
            onClick={openModal}
            className="create-button"
          >
            新增流水
          </Button>
        </div>

        {/* 统计卡片 */}
        <div className="stats-container">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={24}>
              <Card className="stat-card stat-total">
                <div className="stat-header">
                  <div className="stat-icon total">
                    <FileTextOutlined />
                  </div>
                  <div className="stat-label">总记录数</div>
                </div>
                <div className="stat-value" style={{ color: "#1890ff" }}>
                  {stats.total_records} <span className="stat-unit">条</span>
                </div>
              </Card>
            </Col>
          </Row>
        </div>

        {/* 流水列表 */}
        <Card className="transaction-list-card">
          <div className="filter-toolbar">
            <Space size="middle" wrap>
              <Input
                placeholder="账号名称"
                value={searchParams.account}
                onChange={(e) =>
                  setSearchParams({ ...searchParams, account: e.target.value })
                }
                onPressEnter={handleSearch}
                prefix={<Search size={16} />}
                style={{ width: 150 }}
                allowClear
              />
              <Select
                placeholder="交易所"
                value={searchParams.exchange || undefined}
                onChange={(value) =>
                  setSearchParams({ ...searchParams, exchange: value || "" })
                }
                style={{ width: 120 }}
                allowClear
              >
                {exchanges.map((ex) => (
                  <Option key={ex.code || ex.name} value={ex.code || ex.name}>
                    {ex.name}
                  </Option>
                ))}
              </Select>
              <Select
                placeholder="交易类型"
                value={searchParams.trade_type || undefined}
                onChange={(value) =>
                  setSearchParams({ ...searchParams, trade_type: value || "" })
                }
                style={{ width: 100 }}
                allowClear
              >
                <Option value="spot">现货</Option>
                <Option value="swap">合约</Option>
              </Select>
              <Input
                placeholder="资产"
                value={searchParams.asset}
                onChange={(e) =>
                  setSearchParams({ ...searchParams, asset: e.target.value })
                }
                onPressEnter={handleSearch}
                style={{ width: 100 }}
                allowClear
              />
              <Select
                placeholder="变动类型"
                value={searchParams.type || undefined}
                onChange={(value) =>
                  setSearchParams({ ...searchParams, type: value || "" })
                }
                style={{ width: 120 }}
                allowClear
              >
                {transactionTypes.map((t) => (
                  <Option key={t.value} value={t.value}>
                    {t.label}
                  </Option>
                ))}
              </Select>
              <DatePicker.RangePicker
                value={[
                  searchParams.date_from ? dayjs(searchParams.date_from) : null,
                  searchParams.date_to ? dayjs(searchParams.date_to) : null,
                ]}
                onChange={(dates) =>
                  setSearchParams({
                    ...searchParams,
                    date_from: dates?.[0]?.format("YYYY-MM-DD") || "",
                    date_to: dates?.[1]?.format("YYYY-MM-DD") || "",
                  })
                }
                style={{ width: 240 }}
              />
              <Button type="primary" onClick={handleSearch} icon={<Search size={16} />}>
                搜索
              </Button>
              <Button onClick={handleReset}>重置</Button>
              <Button icon={<RefreshCw size={16} />} onClick={loadTransactions}>
                刷新
              </Button>
            </Space>
          </div>
          <Table
            columns={columns}
            dataSource={transactions}
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`,
              pageSizeOptions: ["20", "50", "100"],
            }}
            onChange={handleTableChange}
            rowKey="id"
            scroll={{ x: "max-content" }}
            className="transaction-table"
            size="small"
          />
        </Card>

        {/* 新增流水 Modal */}
        <Modal
          title={
            <div className="modal-title">
              {editingId ? <Edit size={20} /> : <Plus size={20} />}
              <span>{editingId ? "编辑流水记录" : "新增流水记录"}</span>
            </div>
          }
          open={showModal}
          onCancel={() => {
            setShowModal(false);
            setEditingId(null);
            form.resetFields();
          }}
          footer={null}
          className="transaction-modal"
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            onValuesChange={handleFormValuesChange}
            initialValues={{
              trade_type: "spot",
              type: "deposit",
              transacted_at: dayjs(),
            }}
            className="transaction-form"
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="账号名称"
                  name="account"
                  rules={[{ required: true, message: "请选择账号名称" }]}
                >
                  <Select
                    showSearch
                    placeholder="选择账号"
                    filterOption={(input, option) =>
                      (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                    options={[...new Set(accountConfigs.map((c) => c.account))].map((a) => ({
                      label: a,
                      value: a,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="交易所"
                  name="exchange"
                  rules={[{ required: true, message: "请选择交易所" }]}
                >
                  <Select placeholder="选择交易所">
                    {(formAccount
                      ? [...new Set(accountConfigs.filter((c) => c.account === formAccount).map((c) => c.exchange))]
                      : exchanges.map((ex) => ex.code || ex.name)
                    ).map((ex) => (
                      <Option key={ex} value={ex}>{ex}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label={isInternalTransferMode ? "转出方（资金减少）" : "交易类型"}
                  name="trade_type"
                  rules={[
                    {
                      required: true,
                      message: isInternalTransferMode ? "请选择转出方" : "请选择交易类型",
                    },
                  ]}
                  extra={
                    isInternalTransferMode && formTradeType
                      ? `转入方（资金增加）：${transferToTradeType === "spot" ? "现货" : "合约"}`
                      : undefined
                  }
                >
                  <Select placeholder={isInternalTransferMode ? "选择转出方" : "选择交易类型"}>
                    {(formAccount && formExchange
                      ? [...new Set(
                          accountConfigs
                            .filter((c) => c.account === formAccount && c.exchange === formExchange)
                            .map((c) => c.trade_type),
                        )]
                      : ["spot", "swap"]
                    ).map((tt) => (
                      <Option key={tt} value={tt}>{tt === "spot" ? "现货" : "合约"}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="资产"
                  name="asset"
                  rules={[{ required: true, message: "请输入资产名称" }]}
                >
                  <Input placeholder="例如：USDT、BTC、ETH" />
                </Form.Item>
              </Col>
            </Row>

            {transferTargetMissing && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message={`该账号在 ${formExchange} 下缺少${transferToTradeType === "spot" ? "现货" : "合约"}配置，请先在账号配置中创建转入方配置后再划转`}
              />
            )}

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="变动类型"
                  name="type"
                  rules={[{ required: true, message: "请选择变动类型" }]}
                >
                  <Select placeholder="选择变动类型">
                    {transactionTypes.map((t) => (
                      <Option key={t.value} value={t.value}>
                        {t.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={isInternalTransferMode ? "划转数量" : "变动金额"}
                  name="amount"
                  rules={[
                    {
                      required: true,
                      message: isInternalTransferMode ? "请输入划转数量" : "请输入变动金额",
                    },
                    ...(isInternalTransferMode
                      ? [
                          {
                            validator: (_, value) =>
                              value == null || value > 0
                                ? Promise.resolve()
                                : Promise.reject(new Error("划转数量必须大于 0")),
                          },
                        ]
                      : []),
                  ]}
                  extra={
                    isInternalTransferMode
                      ? "输入正数；转出方资金减少、转入方资金增加"
                      : "正数表示增加，负数表示减少"
                  }
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder={isInternalTransferMode ? "划转数量" : "变动金额"}
                    precision={8}
                    min={isInternalTransferMode ? 0 : undefined}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label={isInternalTransferMode ? "转出方变动前余额" : "变动前余额"}
                  name="balance_before"
                  extra={!editingId ? "填写账号信息后自动计算" : undefined}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder={balanceFetching ? "查询中…" : "自动计算"}
                    precision={8}
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={isInternalTransferMode ? "转出方变动后余额" : "变动后余额"}
                  name="balance_after"
                  extra={!editingId ? "填写变动金额后自动计算" : undefined}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder="自动计算"
                    precision={8}
                    disabled
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item label="交易时间" name="transacted_at">
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm:ss"
                style={{ width: "100%" }}
              />
            </Form.Item>

            <Form.Item label="备注" name="remark">
              <TextArea rows={2} placeholder="备注信息（可选）" />
            </Form.Item>

            <Form.Item className="form-actions">
              <Space>
                <Button
                  onClick={() => {
                    setShowModal(false);
                    setEditingId(null);
                    form.resetFields();
                  }}
                >
                  取消
                </Button>
                <Button type="primary" htmlType="submit" disabled={transferTargetMissing}>
                  {editingId ? "保存" : isInternalTransferMode ? "确认划转" : "创建"}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </ErrorBoundary>
  );
};

export default TransactionList;
